import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Bot, Loader2, MessageSquare, Send, User, UserCheck } from 'lucide-react';
import Button from '../components/ui/Button';
import { api } from '../services/api';
import { handleApiError } from '../utils/errorHandling';
import { useSubscriptionError } from '../context/SubscriptionErrorContext';
import { InboxEntry, InboxTranscriptEntry, StorefrontProduct } from '../types';
import ConversationProductGrid from '../components/ConversationProductGrid';
import { formatMessageMarkdown } from '../utils/messageMarkdown';

type Filter = 'waiting' | 'live' | 'all';

/**
 * The shopper inbox.
 *
 * Two things happen on this screen and they are the same thing: reading what customers asked, and
 * answering the ones who asked for a person. Everything is polled — a handoff is a live
 * conversation, and a merchant staring at a stale list will answer someone who left five minutes
 * ago. The intervals are deliberately different: the list is a count that can lag a little, the
 * open transcript is a conversation that cannot.
 */
const LIST_POLL_MS = 5000;
const TRANSCRIPT_POLL_MS = 3000;

const FILTERS: { id: Filter; label: string }[] = [
    { id: 'waiting', label: 'Waiting' },
    { id: 'live', label: 'Live' },
    { id: 'all', label: 'All' },
];

const authorStyles: Record<InboxTranscriptEntry['author'], string> = {
    shopper: 'bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-gray-100',
    assistant: 'bg-primary/10 text-gray-900 dark:text-gray-100 border border-primary/30',
    agent: 'bg-primary text-white',
    system: 'bg-transparent text-gray-500 dark:text-gray-400 border border-dashed border-gray-300 dark:border-gray-600 text-xs',
};

const waitedFor = (seconds?: number): string => {
    if (seconds === undefined) return '';
    if (seconds < 60) return `${seconds}s`;
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}m`;
    return `${Math.floor(minutes / 60)}h ${minutes % 60}m`;
};

const InboxPage: React.FC = () => {
    const [filter, setFilter] = useState<Filter>('waiting');
    const [entries, setEntries] = useState<InboxEntry[]>([]);
    const [waiting, setWaiting] = useState(0);
    const [selectedId, setSelectedId] = useState<string | null>(null);
    const [transcript, setTranscript] = useState<InboxTranscriptEntry[]>([]);
    // Every product the open transcript showed, resolved once and looked up per message. A card is
    // catalogue data, so the same SKU named in three messages costs one entry, not three requests.
    const [productsBySku, setProductsBySku] = useState<Record<string, StorefrontProduct>>({});
    const [reply, setReply] = useState('');
    const [isSending, setIsSending] = useState(false);
    const [isLoadingList, setIsLoadingList] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const { setSubscriptionError } = useSubscriptionError();
    const transcriptEndRef = useRef<HTMLDivElement | null>(null);

    const selected = entries.find(entry => entry.conversationId === selectedId) ?? null;
    const heldByMe = selected?.handoff?.status === 'active';

    const loadList = useCallback(async () => {
        try {
            const response = await api.getInbox(filter);
            setEntries(response.data);
            setWaiting(response.waiting);
            setError(null);
        } catch (err) {
            handleApiError(err, setError, setSubscriptionError);
        } finally {
            setIsLoadingList(false);
        }
    }, [filter, setSubscriptionError]);

    /**
     * Fetch the cards for every product the transcript named, in one call.
     *
     * Failure is silent on purpose: the merchant came here to read a conversation, and a catalogue
     * lookup that did not answer should cost them the pictures, not the transcript.
     */
    const hydrateProducts = useCallback(async (entries: InboxTranscriptEntry[]) => {
        const skus = Array.from(new Set(entries.flatMap(entry => entry.productSkus || [])));
        if (skus.length === 0) {
            setProductsBySku({});
            return;
        }
        try {
            const { products } = await api.resolveProducts(skus);
            setProductsBySku(Object.fromEntries(products.map(product => [product.sku, product])));
        } catch {
            setProductsBySku({});
        }
    }, []);

    const loadTranscript = useCallback(async (conversationId: string) => {
        try {
            const response = await api.getInboxTranscript(conversationId);
            setTranscript(response.messages);
            void hydrateProducts(response.messages);
        } catch (err) {
            handleApiError(err, setError, setSubscriptionError);
        }
    }, [setSubscriptionError, hydrateProducts]);

    useEffect(() => {
        setIsLoadingList(true);
        loadList();
        const timer = setInterval(loadList, LIST_POLL_MS);
        return () => clearInterval(timer);
    }, [loadList]);

    useEffect(() => {
        if (!selectedId) return;
        loadTranscript(selectedId);
        const timer = setInterval(() => loadTranscript(selectedId), TRANSCRIPT_POLL_MS);
        return () => clearInterval(timer);
    }, [selectedId, loadTranscript]);

    useEffect(() => {
        transcriptEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [transcript]);

    const claim = async () => {
        if (!selectedId) return;
        try {
            await api.claimHandoff(selectedId);
            await Promise.all([loadList(), loadTranscript(selectedId)]);
        } catch (err) {
            // A refused claim is normal, not an outage: someone else took it a second earlier, and
            // the message names them.
            handleApiError(err, setError, setSubscriptionError);
        }
    };

    const release = async () => {
        if (!selectedId) return;
        try {
            await api.releaseHandoff(selectedId);
            await Promise.all([loadList(), loadTranscript(selectedId)]);
        } catch (err) {
            handleApiError(err, setError, setSubscriptionError);
        }
    };

    const send = async () => {
        const content = reply.trim();
        if (!content || !selectedId) return;

        setIsSending(true);
        try {
            await api.replyAsAgent(selectedId, content);
            setReply('');
            await loadTranscript(selectedId);
        } catch (err) {
            handleApiError(err, setError, setSubscriptionError);
        } finally {
            setIsSending(false);
        }
    };

    return (
        <div className="flex flex-col h-full">
            <div className="mb-4">
                <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Conversations</h1>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                    What your customers asked, and the ones waiting for a person.
                </p>
            </div>

            {error && (
                <div className="mb-3 rounded-md border border-red-300 bg-red-50 dark:bg-red-900/20 px-3 py-2 text-sm text-red-700 dark:text-red-300">
                    {error}
                </div>
            )}

            <div className="flex gap-4 flex-1 min-h-0">
                <aside className="w-80 flex-shrink-0 flex flex-col rounded-xl border border-gray-200 dark:border-border-color bg-white dark:bg-surface overflow-hidden">
                    <div className="flex border-b border-gray-200 dark:border-border-color">
                        {FILTERS.map(option => (
                            <button
                                key={option.id}
                                onClick={() => setFilter(option.id)}
                                className={`flex-1 px-3 py-2 text-sm font-medium transition-colors ${
                                    filter === option.id
                                        ? 'text-primary border-b-2 border-primary'
                                        : 'text-gray-500 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200'
                                }`}
                            >
                                {option.label}
                                {option.id === 'waiting' && waiting > 0 && (
                                    <span className="ml-1.5 rounded-full bg-red-500 px-1.5 py-0.5 text-[10px] font-bold text-white">
                                        {waiting}
                                    </span>
                                )}
                            </button>
                        ))}
                    </div>

                    <div className="flex-1 overflow-y-auto">
                        {isLoadingList && entries.length === 0 && (
                            <div className="flex items-center justify-center p-6 text-gray-400">
                                <Loader2 className="h-5 w-5 animate-spin" />
                            </div>
                        )}

                        {!isLoadingList && entries.length === 0 && (
                            <p className="p-6 text-center text-sm text-gray-500 dark:text-gray-400">
                                {filter === 'waiting'
                                    ? 'Nobody is waiting for a person.'
                                    : 'No conversations here yet.'}
                            </p>
                        )}

                        {entries.map(entry => (
                            <button
                                key={entry.conversationId}
                                onClick={() => setSelectedId(entry.conversationId)}
                                className={`w-full border-b border-gray-100 dark:border-border-color px-3 py-3 text-left transition-colors ${
                                    selectedId === entry.conversationId
                                        ? 'bg-primary/5'
                                        : 'hover:bg-gray-50 dark:hover:bg-gray-800'
                                }`}
                            >
                                <div className="flex items-center justify-between gap-2">
                                    <span className="truncate text-xs font-mono text-gray-500 dark:text-gray-400">
                                        {entry.shopperId}
                                    </span>
                                    {entry.handoff?.status === 'requested' && (
                                        <span className="flex-shrink-0 rounded-full bg-red-100 dark:bg-red-900/40 px-2 py-0.5 text-[10px] font-semibold text-red-700 dark:text-red-300">
                                            {waitedFor(entry.handoff.waitingSeconds)}
                                        </span>
                                    )}
                                    {entry.handoff?.status === 'active' && (
                                        <span className="flex-shrink-0 rounded-full bg-green-100 dark:bg-green-900/40 px-2 py-0.5 text-[10px] font-semibold text-green-700 dark:text-green-300">
                                            live
                                        </span>
                                    )}
                                </div>
                                <p className="mt-1 line-clamp-2 text-sm text-gray-800 dark:text-gray-200">
                                    {entry.lastMessagePreview || '—'}
                                </p>
                                {entry.handoff?.reason && (
                                    <p className="mt-1 truncate text-xs italic text-gray-500 dark:text-gray-400">
                                        “{entry.handoff.reason}”
                                    </p>
                                )}
                            </button>
                        ))}
                    </div>
                </aside>

                <section className="flex flex-1 flex-col rounded-xl border border-gray-200 dark:border-border-color bg-white dark:bg-surface overflow-hidden">
                    {!selected && (
                        <div className="flex flex-1 flex-col items-center justify-center text-gray-400">
                            <MessageSquare className="mb-2 h-8 w-8" />
                            <p className="text-sm">Pick a conversation to read it.</p>
                        </div>
                    )}

                    {selected && (
                        <>
                            <header className="flex items-center justify-between gap-3 border-b border-gray-200 dark:border-border-color px-4 py-3">
                                <div className="min-w-0">
                                    <p className="truncate font-mono text-xs text-gray-500 dark:text-gray-400">
                                        {selected.shopperId}
                                    </p>
                                    {selected.handoff?.status === 'active' && (
                                        <p className="text-xs text-green-600 dark:text-green-400">
                                            Held by {selected.handoff.agentName || 'an agent'}
                                        </p>
                                    )}
                                </div>
                                {selected.handoff?.status === 'requested' && (
                                    <Button size="sm" onClick={claim}>
                                        <UserCheck className="mr-1.5 h-4 w-4" /> Take it
                                    </Button>
                                )}
                                {heldByMe && (
                                    <Button size="sm" variant="secondary" onClick={release}>
                                        Hand back to the assistant
                                    </Button>
                                )}
                            </header>

                            <div className="flex-1 space-y-3 overflow-y-auto p-4">
                                {transcript.map(entry => (
                                    <div
                                        key={entry.id}
                                        className={`flex ${entry.author === 'shopper' ? 'justify-start' : entry.author === 'system' ? 'justify-center' : 'justify-end'}`}
                                    >
                                        <div className={`max-w-[75%] rounded-lg px-3 py-2 text-sm ${authorStyles[entry.author]}`}>
                                            {entry.author !== 'system' && (
                                                <div className="mb-1 flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide opacity-70">
                                                    {entry.author === 'shopper' && <User className="h-3 w-3" />}
                                                    {entry.author === 'assistant' && <Bot className="h-3 w-3" />}
                                                    {entry.author === 'agent' && <UserCheck className="h-3 w-3" />}
                                                    {entry.author === 'agent'
                                                        ? entry.agentName || 'You'
                                                        : entry.author}
                                                </div>
                                            )}
                                            {entry.author === 'assistant' ? (
                                                // The assistant writes markdown and the widget
                                                // renders it. Showing the raw source here meant the
                                                // merchant read `**110€**` where the shopper read a
                                                // price. Only the assistant's own lines: a shopper's
                                                // message is their words, and a merchant's reply is
                                                // typed into a plain box.
                                                <div
                                                    className="ai-message-content"
                                                    dangerouslySetInnerHTML={{
                                                        __html: formatMessageMarkdown(entry.content)
                                                    }}
                                                />
                                            ) : (
                                                <p className="whitespace-pre-wrap">{entry.content}</p>
                                            )}
                                            <ConversationProductGrid
                                                products={(entry.productSkus || [])
                                                    .map(sku => productsBySku[sku])
                                                    .filter(Boolean)}
                                            />
                                        </div>
                                    </div>
                                ))}
                                <div ref={transcriptEndRef} />
                            </div>

                            <footer className="border-t border-gray-200 dark:border-border-color p-3">
                                {heldByMe ? (
                                    <div className="flex gap-2">
                                        <input
                                            value={reply}
                                            onChange={event => setReply(event.target.value)}
                                            onKeyDown={event => {
                                                if (event.key === 'Enter' && !event.shiftKey) {
                                                    event.preventDefault();
                                                    send();
                                                }
                                            }}
                                            placeholder="Write to the customer…"
                                            maxLength={4000}
                                            className="flex-1 rounded-md border border-gray-300 dark:border-border-color bg-white dark:bg-gray-800 px-3 py-2 text-sm text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary"
                                        />
                                        <Button onClick={send} isLoading={isSending} disabled={!reply.trim()}>
                                            <Send className="h-4 w-4" />
                                        </Button>
                                    </div>
                                ) : (
                                    <p className="text-center text-xs text-gray-500 dark:text-gray-400">
                                        {selected.handoff?.status === 'requested'
                                            ? 'Take the conversation to answer in it.'
                                            : 'The assistant is handling this conversation.'}
                                    </p>
                                )}
                            </footer>
                        </>
                    )}
                </section>
            </div>
        </div>
    );
};

export default InboxPage;
